/// <amd-module name="Core/Medius.Core.Web/Scripts/components/emailStatusTab/common/emailStatusRenderer"/>
import * as React from 'react';
import { FlexLayout } from '@medius/ui-controls';
import { translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';
import { RecipientGroup, RecipientDisplayItem } from './recipientGroup';
import { GenericRecipient, separateRecipientsByType } from './recipientUtils';
import { EmailStatusIcon } from './emailStatusIcon';
import { EmailStatus } from '../models/emailEntryDto';
import { createStatusTooltip, getEmailStatusTranslationKey } from './emailStatusUtils';

export interface EmailStatusRendererRecipient extends GenericRecipient {
    status: EmailStatus;
    latestEventReason?: string;
}

export interface EmailStatusRendererProps {
    recipients: EmailStatusRendererRecipient[];
    layout?: 'flex' | 'grid';
    labels?: {
        to?: string;
        cc?: string;
        bcc?: string;
    };
}

export const EmailStatusRenderer = ({
    recipients,
    layout = 'flex',
    labels,
}: EmailStatusRendererProps): React.ReactElement => {
    const defaultLabels = {
        to: translate('#Core/emailTo'),
        cc: translate('#Core/emailCc'),
        bcc: translate('#Core/emailBcc'),
    };
    const actualLabels = labels || defaultLabels;
    const createRecipientDisplayItem = (recipient: EmailStatusRendererRecipient): RecipientDisplayItem => ({
        emailAddress: recipient.emailAddress,
        statusIcon: (
            <EmailStatusIcon
                status={recipient.status}
                tooltip={createStatusTooltip(
                    translate(getEmailStatusTranslationKey(recipient.status)),
                    recipient.latestEventReason
                )}
            />
        ),
    });

    const { toRecipients, ccRecipients, bccRecipients } = separateRecipientsByType(recipients);

    return (
        <FlexLayout direction="column" itemRowSpacing={layout === 'grid' ? 'none' : 'small'}>
            <RecipientGroup
                label={actualLabels.to}
                recipients={toRecipients.map(createRecipientDisplayItem)}
                layout={layout}
            />
            <RecipientGroup
                label={actualLabels.cc}
                recipients={ccRecipients.map(createRecipientDisplayItem)}
                layout={layout}
            />
            <RecipientGroup
                label={actualLabels.bcc}
                recipients={bccRecipients.map(createRecipientDisplayItem)}
                layout={layout}
            />
        </FlexLayout>
    );
};
